import { useFormContext } from "react-hook-form";
import { useLingui } from "@lingui/react/macro";
import { activityRequirementStatusSchema } from "@medoma/models/activities";
import ErrorMessage from "@medoma/components/ErrorMessage/ErrorMessage";
import { Text } from "@medoma/components/Text/Text";
import type { IActivity } from "@/api/Activities";
import Checkbox from "@/components/Checkbox/Checkbox";
import Checkboxes from "@/components/Checkbox/Checkboxes";
import { CategoryIcon } from "@/components/CategoryIcon/CategoryIcon";
import { CompetenceChip } from "@/components/Chips/CompetenceChip";
import { Frequency, Times } from "../activities/details/ScheduleSummary";
import styles from "./ActivitiesToClose.module.scss";

export type IActivitiesToCloseFields = {
  activityIds: string[];
  activitiesAreClosed: boolean;
};

const ActivityLabel = ({ activity }: { activity: IActivity }) => {
  const requiredCompetences =
    "requiredCompetences" in activity ? activity.requiredCompetences : [];
  return (
    <div className={styles.activity}>
      <CategoryIcon category={activity.category} size="small" />
      <div className={styles.details}>
        <Text element="span" weight="medium">
          {activity.title}
        </Text>
        <div className={styles.scheduleRow}>
          <Text element="span" size="small" color="faded">
            <Frequency schedule={activity.schedule} />
            {" · "}
            <Times schedule={activity.schedule} />
          </Text>
          {requiredCompetences.map((competence) => (
            <CompetenceChip
              key={competence}
              competence={competence}
              state={activityRequirementStatusSchema.enum.neutral}
              size="tiny"
            />
          ))}
        </div>
      </div>
    </div>
  );
};

const ActivityCheckbox = ({ activity }: { activity: IActivity }) => {
  const { register } = useFormContext<IActivitiesToCloseFields>();
  return (
    <Checkbox
      {...register("activityIds")}
      label={{
        text: activity.title,
        component: <ActivityLabel activity={activity} />,
      }}
      value={activity.id}
      checkboxPosition="center"
    />
  );
};

/**
 * Lets the user choose which of the patient's activities to close.
 * When the activities could not be fetched, the user instead has to confirm
 * that they have closed them on their own.
 */
export const ActivitiesToClose = ({
  activities,
  couldNotFetchActivities,
  instructions,
}: {
  activities: IActivity[];
  couldNotFetchActivities: boolean;
  instructions: string;
}) => {
  const { t } = useLingui();
  const {
    formState: { errors },
    register,
  } = useFormContext<IActivitiesToCloseFields>();

  if (couldNotFetchActivities) {
    return (
      <>
        <ErrorMessage
          message={t`Kunde inte hämta information om ej avslutade aktiviteter. Du måste därför själv ansvara för att säkerställa att alla aktiviteter är avslutade.`}
        />
        <Checkbox
          errorMessage={errors.activitiesAreClosed?.message}
          label={{ text: t`Patientens aktiviteter är avslutade` }}
          {...register("activitiesAreClosed", {
            required: {
              value: true,
              message: t`Bekräfta att aktiviteterna har avslutats`,
            },
          })}
        />
      </>
    );
  }

  if (activities.length === 0) {
    return null;
  }

  const recurringActivities = activities.filter(({ recurring }) => recurring);
  const oneTimeActivities = activities.filter(({ recurring }) => !recurring);

  return (
    <div className={styles.activitiesSection}>
      <Text element="p" weight="medium">
        {instructions}
      </Text>
      {recurringActivities.length > 0 ? (
        <Checkboxes legend={t`Återkommande aktiviteter`}>
          {recurringActivities.map((activity) => (
            <ActivityCheckbox key={activity.id} activity={activity} />
          ))}
        </Checkboxes>
      ) : null}
      {oneTimeActivities.length > 0 ? (
        <Checkboxes legend={t`Engångsaktiviteter`}>
          {oneTimeActivities.map((activity) => (
            <ActivityCheckbox key={activity.id} activity={activity} />
          ))}
        </Checkboxes>
      ) : null}
    </div>
  );
};
